import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API, BACKEND_URL } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { CountdownTimer } from './CountdownTimer';

const BIDDER_COLORS = [
  '#0a7e8c', '#e74c3c', '#2ecc71', '#9b59b6', '#f39c12',
  '#1abc9c', '#e67e22', '#3498db', '#e91e63', '#00bcd4',
  '#8bc34a', '#ff5722', '#673ab7', '#009688', '#ff9800',
  '#795548', '#607d8b', '#4caf50', '#f44336', '#2196f3',
  '#c2185b', '#00695c', '#4527a0', '#bf360c', '#0277bd',
  '#558b2f', '#ad1457', '#1565c0', '#6a1b9a', '#e65100',
  '#2e7d32', '#283593', '#d84315', '#00838f', '#9e9d24',
  '#4e342e', '#37474f', '#c62828', '#1b5e20', '#4a148c',
  '#ff6f00', '#006064', '#880e4f', '#33691e', '#01579b',
  '#3e2723', '#827717', '#b71c1c', '#004d40', '#311b92'
];

const getBidderColor = (bidderNumber) => {
  if (!bidderNumber) return BIDDER_COLORS[0];
  return BIDDER_COLORS[(bidderNumber - 1) % BIDDER_COLORS.length];
};

const BidModal = ({ auction, onClose, onBidPlaced }) => {
  const [bidAmount, setBidAmount] = useState(0);
  const [bidding, setBidding] = useState(false);
  const [error, setError] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [bids, setBids] = useState([]);
  const [liveAuction, setLiveAuction] = useState(auction);
  const { user } = useAuth();
  const wsRef = useRef(null);

  const minBid = (liveAuction.current_price || 0) + (liveAuction.min_increment || 100);

  const loadAuction = () => axios.get(`${API}/auctions/${auction.auction_id}`).then(res => {
    setLiveAuction(res.data);
    setBids(res.data.bids || []);
    setBidAmount(res.data.current_price + res.data.min_increment);
  }).catch(() => {});

  useEffect(() => {
    setBidAmount(minBid);
    loadAuction();

    // WebSocket
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = BACKEND_URL.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}//${wsHost}/api/ws/auction/${auction.auction_id}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'new_bid') {
        setLiveAuction(prev => ({
          ...prev,
          current_price: data.auction.current_price,
          total_bids: data.auction.total_bids,
          end_time: data.auction.end_time
        }));
        setBids(prev => prev.some(b => b.bid_id === data.bid.bid_id) ? prev : [data.bid, ...prev]);
        setBidAmount(data.auction.current_price + (liveAuction.min_increment || 100));
      }
    };

    const ping = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send('ping');
    }, 30000);

    return () => {
      clearInterval(ping);
      if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); }
    };
  }, [auction.auction_id]);

  const handleBid = async () => {
    setError(''); setShowConfirm(false); setBidding(true);
    try {
      await axios.post(`${API}/auctions/${auction.auction_id}/bid`, {
        auction_id: auction.auction_id, amount: bidAmount
      });
      loadAuction();
      if (onBidPlaced) onBidPlaced();
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore');
    } finally { setBidding(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="bid-modal-overlay">
      <div className="bg-white rounded-xl max-w-md w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="bid-modal">
        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b">
          {auction.horse?.images?.[0] && (
            <img src={auction.horse.images[0]} alt="" className="w-12 h-12 rounded object-cover" />
          )}
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm">Offerta</p>
            <p className="text-xs text-[#64748b] truncate">{auction.horse?.name || auction.title}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none" data-testid="bid-modal-close">&times;</button>
        </div>

        {/* Status & Timer */}
        <div className="p-4 text-center border-b">
          {liveAuction.status === 'active' ? (
            <>
              <CountdownTimer endTime={liveAuction.end_time} />
              <p className="text-2xl md:text-3xl font-bold text-[#0a1628] mt-2">{'\u20AC'}{liveAuction.current_price?.toLocaleString()}</p>
              <p className="text-xs text-[#64748b] mt-1">{liveAuction.total_bids} offerte</p>
            </>
          ) : (
            <>
              <p className="text-sm text-[#64748b]">L'asta &egrave; terminata</p>
              <p className="font-bold">Completato</p>
              <p className="text-2xl md:text-3xl font-bold text-[#0a1628] mt-2">{'\u20AC'}{liveAuction.current_price?.toLocaleString()}</p>
            </>
          )}
        </div>

        {/* Bid Controls - only when active */}
        {liveAuction.status === 'active' && (
          <div className="p-4 border-b">
            <div className="flex items-center justify-center gap-3 mb-3">
              <button onClick={() => setBidAmount(prev => Math.max(minBid, prev - (liveAuction.min_increment || 100)))} className="w-10 h-10 rounded-full border-2 border-gray-300 text-lg font-bold hover:border-[#c9a227] transition-colors" data-testid="modal-bid-minus">-</button>
              <div className="text-center px-3">
                <p className="text-xl font-bold">{'\u20AC'}{bidAmount.toLocaleString()}</p>
              </div>
              <button onClick={() => setBidAmount(prev => prev + (liveAuction.min_increment || 100))} className="w-10 h-10 rounded-full border-2 border-gray-300 text-lg font-bold hover:border-[#c9a227] transition-colors" data-testid="modal-bid-plus">+</button>
            </div>
            {!showConfirm ? (
              <button
                onClick={() => {
                  if (!user) { window.location.href = '/login'; return; }
                  if (!user.is_verified) { setError('Account non verificato. Invia documenti a info@equusprime.it'); return; }
                  setShowConfirm(true);
                }}
                className="w-full bg-[#0a7e8c] text-white py-3 rounded-lg font-bold hover:bg-[#086a76] transition-colors"
                data-testid="modal-bid-btn"
              >
                Fai offerta a {'\u20AC'}{bidAmount.toLocaleString()}
              </button>
            ) : (
              <div className="flex gap-2">
                <button onClick={() => setShowConfirm(false)} className="flex-1 py-3 border border-gray-300 rounded-lg text-gray-600 font-medium" data-testid="modal-bid-cancel">Annulla</button>
                <button onClick={handleBid} disabled={bidding} className="flex-1 py-3 bg-[#0a7e8c] text-white rounded-lg font-bold disabled:opacity-50" data-testid="modal-bid-confirm">
                  {bidding ? 'Invio...' : 'Conferma'}
                </button>
              </div>
            )}
            {error && <p className="text-red-500 text-xs mt-2 text-center">{error}</p>}
            {!user && <p className="text-xs text-[#64748b] mt-2 text-center"><a href="/login" className="text-[#c9a227]">Accedi</a> per fare un'offerta</p>}
          </div>
        )}

        {/* Bid History */}
        <div className="p-4">
          <p className="font-bold text-sm mb-3 text-center">Cronologia delle offerte</p>
          {bids.length === 0 ? (
            <p className="text-xs text-[#64748b] text-center">Nessuna offerta</p>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              <div className="grid grid-cols-3 text-xs text-[#64748b] font-medium border-b pb-1">
                <span>Offerente</span><span>Tempo</span><span className="text-right">Offerta</span>
              </div>
              {bids.map((bid, idx) => {
                const isMe = user && bid.user_id === user.user_id;
                const bidderColor = getBidderColor(bid.bidder_number);
                return (
                <div key={bid.bid_id || idx} className="grid grid-cols-3 text-xs items-center py-1 border-b border-gray-100">
                  <span className="flex items-center gap-1">
                    {isMe ? (
                      <span className="w-5 h-5 rounded-full bg-[#c9a227] flex items-center justify-center flex-shrink-0">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
                      </span>
                    ) : (
                      <span className="w-5 h-5 rounded-full text-white text-[10px] flex items-center justify-center font-bold" style={{backgroundColor: bidderColor}}>{bid.bidder_number || '?'}</span>
                    )}
                    <span className="truncate">{isMe ? 'Tu' : `Offerente ${bid.bidder_number || '?'}`}</span>
                  </span>
                  <span className="text-[#64748b]">{bid.created_at ? new Date(bid.created_at).toLocaleTimeString('it-IT', {hour:'2-digit', minute:'2-digit'}) : ''}</span>
                  <span className="text-right font-bold">{'\u20AC'}{bid.amount?.toLocaleString()}</span>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BidModal;
